import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Reset link sent to your email!');
    } catch (error) {
      // Don't reveal if email exists or not for security
      setSent(true);
      toast.success('If this email exists, you will receive a reset link.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="pt-28 bg-black min-h-screen" data-testid="forgot-password-success">
        <div className="max-w-md mx-auto px-6 md:px-12 py-24 text-center">
          <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="font-serif text-3xl text-white mb-4">Check Your Email</h1>
          <p className="text-[#999999] mb-8">
            If an account exists with <span className="text-white">{email}</span>, you will receive a password reset link shortly.
          </p>
          <p className="text-[#666] text-sm mb-8">
            Don't see it? Check your spam folder.
          </p>
          <Link to="/login">
            <Button className="bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-none py-4 px-8">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 bg-black min-h-screen" data-testid="forgot-password-page">
      <div className="max-w-md mx-auto px-6 md:px-12 py-24">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-[#1a1a1a] rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-2">Forgot Password?</h1>
          <p className="text-[#999999]">Enter your email and we'll send you a reset link.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Email
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="rounded-none border-[#1a1a1a] focus:border-[#1A1A1A] py-6"
              data-testid="forgot-email-input"
            />
          </div>

          <Button 
            type="submit"
            disabled={loading}
            className="w-full bg-[#D4AF37] text-black hover:bg-[#C9A030] rounded-none py-6 text-sm uppercase tracking-widest font-semibold"
            data-testid="forgot-submit-button"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <span className="spinner mr-2" />
                Sending...
              </span>
            ) : (
              <>
                Send Reset Link
                <ArrowRight className="ml-2 w-4 h-4" />
              </>
            )}
          </Button>
        </form>

        <p className="text-center text-[#999999] mt-8">
          Remember your password?{' '}
          <Link to="/login" className="text-[#D4AF37] hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
